import React from 'react';
import PropTypes from 'prop-types';
import { Link, Box, Spacing, Text } from '@reservamos/elements';
import { useTranslation } from 'react-i18next';
import useWhitelabelFeatures from 'hooks/whitelabel/useWhitelabelFeatures';

const propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  time: PropTypes.string.isRequired,
  brand: PropTypes.string,
  destinationDetails: PropTypes.object,
  hasNextDayBadge: PropTypes.bool,
};

/**
 * EndItem component.
 * @param {Object} props - Component props
 * @param {string} props.title - The title of the end item
 * @param {string} props.description - The description text
 * @param {string} props.time - The time value
 * @param {string} props.brand - The brand identifier
 * @param {Object} props.destinationDetails - Details about the destination
 * @param {boolean} props.hasNextDayBadge - Indicates if any segment has a next day badge
 * @returns {JSX.Element} The end item component
 */
const EndItem = ({ title, description, time, brand, destinationDetails, hasNextDayBadge }) => {
  const { t } = useTranslation('trips');
  const hasCoordinates =
    destinationDetails.coordinates?.lat || destinationDetails.coordinates?.long;
  const features = useWhitelabelFeatures();
  const showTerminal = features.SHOW_TERMINAL_NAME_AT_ITINERARY_ITEM;
  return (
    <div className="route-detail-row departure">
      <div className={`rd-left ${hasNextDayBadge ? 'with-next-day' : ''}`}>
        <Text size="S">{time}</Text>
      </div>

      <div className="rd-center">
        <i className="ic-dot" />
      </div>

      <div className={`rd-right ic-dot ${brand}`}>
        <Box
          paddingVertical="S"
          paddingHorizontal="S"
          borderRadius="S"
          border="all"
          borderColor="grayBorder"
          alphaBg="M"
          hasWidth
          bgColor="grayBorder"
        >
          <Spacing size="XS" vertical>
            <Text weight="bold" size="L">
              {title}
            </Text>
            {showTerminal && <Text size="S">{description}</Text>}
            {destinationDetails.address && <Text size="S">{destinationDetails.address}</Text>}
          </Spacing>
          {hasCoordinates ? (
            <Link
              newTab
              type="primary"
              size="XS"
              href={`https://www.google.com/maps/search/?api=1&query=${destinationDetails.coordinates.lat},${destinationDetails.coordinates.long}`}
              text={t('see_location')}
            />
          ) : null}
        </Box>
      </div>
    </div>
  );
};

EndItem.propTypes = propTypes;

export default EndItem;
